import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { BulkSettlementCreateFor } from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useMemo, useRef, useState } from 'react';

import DescribedMenuItem from '~components/Menu/DescribedMenuItem';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';

import BulkCreateSettlementCTA from './BulkCreateSettlementCTA';
import { getBulkSettlementDateRange } from './bulkSettlementDateRange';
import {
  BULK_SETTLEMENT_ENTITY_TYPES_IN_MENU_ORDER,
  bulkSettlementEntityTypes,
  getBulkSettlementEntityTypeOption,
} from './bulkSettlementEntityTypes';

interface BulkCreateSettlementsMenuProps {
  description?: string;
  endDate?: Dayjs;
  onBulkCreateFinished?: () => void;
  startDate?: Dayjs;
}

export default function BulkCreateSettlementsMenu({
  description,
  endDate,
  onBulkCreateFinished,
  startDate,
}: BulkCreateSettlementsMenuProps) {
  const menuHandler = useRef<MenuHandler>(null);
  const [selectedEntityType, setSelectedEntityType] =
    useState<BulkSettlementCreateFor | null>(null);
  const initialDateRange = useMemo(
    () =>
      startDate && endDate ? getBulkSettlementDateRange(startDate, endDate) : undefined,
    [endDate, startDate],
  );
  const initialEntityTypes = useMemo(
    () =>
      selectedEntityType
        ? [getBulkSettlementEntityTypeOption(selectedEntityType)]
        : undefined,
    [selectedEntityType],
  );

  return (
    <>
      <Menu
        ref={menuHandler}
        menuTrigger={
          <SmallButton
            color="brandV2Black"
            endIcon={<ArrowDropDown />}
            variant="contained"
          >
            {t('approvals.files.bulk_create_settlements')}
          </SmallButton>
        }
      >
        {BULK_SETTLEMENT_ENTITY_TYPES_IN_MENU_ORDER.map((entityType) => (
          <DescribedMenuItem
            key={entityType}
            description={description}
            label={t(bulkSettlementEntityTypes[entityType].menuLabelKey)}
            onClick={() => {
              menuHandler.current?.onClose?.();
              setSelectedEntityType(entityType);
            }}
          />
        ))}
      </Menu>

      {selectedEntityType && (
        <BulkCreateSettlementCTA
          autoOpen
          category={bulkSettlementEntityTypes[selectedEntityType].category}
          entityTypeLocked
          initialDateRange={initialDateRange}
          initialEntityTypes={initialEntityTypes}
          onBulkCreateFinished={onBulkCreateFinished}
          onDialogClose={() => setSelectedEntityType(null)}
        />
      )}
    </>
  );
}
